import ScoringTable, {
  ScoringTableDataRow,
  ScoringTableDef
} from "@/components/controls/DataTable/ScoringTable";
import { Card, CardHeader, CardContent } from "@/components/ui/card";

import UserBreadcrumbs, {
  UserBreadcrumbsProps
} from "@/components/controls/UserBreadcrumbs/UserBreadcrumbs";
import { ScoringTableCardLabelProps } from "./ScoringTableCardController";
import { ReactElement } from "react";
import Text from "@/components/ui/text";
import { Loader, NoDataBadge } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";

export interface ScoringTableCardProps {
  dataTestPrefix: string;
  labelProps: ScoringTableCardLabelProps;
  data: ScoringTableDataRow[] | undefined;
  onRowClick?: (rowData: ScoringTableDataRow) => void;
  breadcrumbsNavProps: UserBreadcrumbsProps;
  headerDataWidget: ReactElement;
  tableDef: ScoringTableDef;
  isLoading?: boolean;
}

const ScoringTableCard = ({
  dataTestPrefix,
  labelProps,
  data,
  onRowClick,
  breadcrumbsNavProps,
  headerDataWidget,
  tableDef,
  isLoading
}: ScoringTableCardProps) => {
  const { empty } = labelProps;
  return (
    <Card
      data-test={`${dataTestPrefix}-card`}
      className="min-w-[512px] max-w-[850px] h-[472px] border-[0.5px] border-border-border1 shadow-none rounded-xl flex flex-col"
    >
      <CardHeader className="flex flex-row flex-nowrap justify-between content-stretch items-start h-28 flex-shrink-0">
        <>
          <div
            data-test={`${dataTestPrefix}-card-titles`}
            className="flex flex-col flex-nowrap items-start justify-between align-start"
          >
            <UserBreadcrumbs {...breadcrumbsNavProps} />
          </div>
          {data && data.length > 0 && breadcrumbsNavProps.ids.length == 1 && (
            <>{headerDataWidget}</>
          )}
        </>
      </CardHeader>
      <CardContent
        className={
          "w-full flex-1 mt-0 p-0 flex flex-col flex-nowrap justify-between items-center content-stretch overflow-hidden min-h-0"
        }
      >
        {isLoading && (
          <div className="flex w-full h-full items-center justify-center">
            <IconContainer size="md" color="cta" nativeStroke>
              <Loader />
            </IconContainer>
          </div>
        )}
        {!isLoading && (!data || data.length === 0) && (
          <div className="relative h-full w-full flex items-center justify-center p-0 top-[-65px]">
            <NoDataBadge />
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none top-1/2">
              <Text as="div" variant="body" className="text-center px-5">
                {empty.title}
              </Text>
              <Text
                as="div"
                variant="bodySecondary"
                className="text-center mt-2 mx-20"
              >
                {empty.subtitle}
              </Text>
            </div>
          </div>
        )}
        {!isLoading && data && data.length > 0 && (
          <div className="w-full h-full overflow-auto">
            <ScoringTable
              data-test={`${dataTestPrefix}-table`}
              data={data}
              onRowClick={onRowClick}
              tableDef={tableDef}
              maxRowsInPage={data.length}
              isLoading={isLoading}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ScoringTableCard;
